/* eslint-disable no-nested-ternary */
import React, { useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Collapse,
  Divider,
  Portal,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';
import { useQuery } from 'react-query';
import { FileDownload } from '@omega-energia/react';
import { PartnerInvoiceStatus, formatAmount } from '@omega-energia/utilities';
import { ParceirosTableRowStyles } from '../Parceiros.style';
import MenuButton from './MenuButton';
import ModalEditarDados from './ModalEditarDados';
import request from '../../../../../services/network/request';
import { useAuth } from '../../../../../auth/authProvider';
import { ParceirosPagamentosTableRow } from './ParceirosPagamentosTableRow';
import ModalAdicionarComissao from './ModalAdicionarComissao';
import ModalAdicionarCampanha from './ModalAdicionarCampanha';
import DownloadIcon from '../../../../Precos/PriceTable/DownloadIcon';
import FileUploader from './FileUploader';
import ModalSupplierData from './ModalSupplierData';
import ModalPurchaseOrderData from './ModalPurchaseOrderData';
import ModalStatusHistory from './ModalStatusHistory';
import ModalClonePurchaseOrder from './ModalClonePurchaseOrder';
import TableColumnSortButton from '../../../../../theme/components/TableColumnSortButton/TableColumnSortButton';
import partnershipRequest from '../../../../../services/network/partnershipRequest';

export function ParceirosTableRow(props) {
  const { token } = useAuth();
  const { invoicesData, refetch } = props;
  const useRowStyles = makeStyles(ParceirosTableRowStyles);
  const classes = useRowStyles();
  const [collapseOpen, setCollapseOpen] = useState(false);
  const [currentModal, setCurrentModal] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [supplierData, setSupplierData] = useState(null);
  const [poData, setPoData] = useState(null);
  const [loadingSupplier, setLoadingSupplier] = useState(false);
  const [loadingPO, setLoadingPO] = useState(false);
  const [loadingStatusHistory, setLoadingStatusHistory] = useState(false);
  const [statusHistoryData, setStatusHistoryData] = useState(null);
  const [sortOrder, setSortOrder] = useState(undefined);
  const [sortOrderInstallment, setSortOrderInstallment] = useState(undefined);

  const FIVE_MINUTES = 300000;
  const hasFile = !!invoicesData?.documentoRepresentanteId;

  const handleCloseModal = () => setCurrentModal(null);

  const { isLoading, data: payments = [], refetch: refetchPayments } = useQuery(
    ['fetch-payments', invoicesData?.id, props.parcelasFilter],
    () => {
      const params = {};
      if (props.parcelasFilter === '1') {
        params.installmentType = 'CONTRACT_SIGNING';
      } else if (props.parcelasFilter === '2') {
        params.installmentType = 'FIRST_BILL_PAID';
      }
      return request.get(`representante/invoices/${invoicesData?.id}/comissioning-leads`, { token, params });
    },
    {
      enabled: collapseOpen,
      staleTime: FIVE_MINUTES,
      cacheTime: FIVE_MINUTES * 2,
    },
  );

  const refetchInvoiceAndPayments = useCallback(() => {
    refetchPayments();
    if (refetch) {
      refetch();
    }
  }, [refetch, refetchPayments]);

  const paymentValidationDependency = useMemo(
    () =>
      payments
        .map(payment => payment?.id)
        .filter(Boolean)
        .sort()
        .join('|'),
    [payments],
  );

  const { data: paymentValidations = [] } = useQuery(
    ['fetch-payment-validations', invoicesData?.id, paymentValidationDependency],
    async () => {
      const response = await partnershipRequest.get('invoices/partner-invoice-comissioning-lead', {
        token,
        params: {
          filters: JSON.stringify({ partnerInvoiceId: invoicesData?.id }),
          offset: 1,
          limit: 1000,
        },
      });

      return Array.isArray(response) ? response : [];
    },
    {
      enabled: collapseOpen && !!invoicesData?.id && payments.length > 0,
      staleTime: FIVE_MINUTES,
      cacheTime: FIVE_MINUTES * 2,
      retry: false,
    },
  );

  const { data: campaigns = [], refetch: refetchCampaigns } = useQuery(
    ['fetch-campaigns', invoicesData?.id, props.parcelasFilter],
    () => request.get('partner-invoice-campaigns', { token, params: { partnerInvoiceId: invoicesData.id } }),
    {
      enabled: collapseOpen && props.parcelasFilter !== '2',
      staleTime: FIVE_MINUTES,
      cacheTime: FIVE_MINUTES * 2,
    },
  );

  const { data: bonuses = [], refetch: refetchBonuses } = useQuery(
    ['fetch-bonuses', invoicesData?.id, props.parcelasFilter],
    () =>
      partnershipRequest.get('invoices/partner-invoice-bonus', {
        token,
        params: { partnerInvoiceId: invoicesData.id },
      }),
    {
      enabled: collapseOpen && props.parcelasFilter !== '2',
      staleTime: FIVE_MINUTES,
      cacheTime: FIVE_MINUTES * 2,
    },
  );

  const { data: referrerData = [] } = useQuery(
    ['fetch-referrer-data', invoicesData?.partner?.accountPublicId, invoicesData?.id, props.parcelasFilter],
    () =>
      request.get(
        `pgp/${invoicesData?.partner?.accountPublicId}/referred-partners-data?invoiceId=${invoicesData?.id}`,
        { token },
      ),
    {
      enabled:
        collapseOpen && !!invoicesData?.partner?.accountPublicId && props.parcelasFilter !== '2' && !!invoicesData?.id,
      staleTime: FIVE_MINUTES,
      cacheTime: FIVE_MINUTES * 2,
    },
  );

  const { data: mgmIndirectData = [], refetch: refetchMGM } = useQuery(
    ['fetch-mgm-indirect-data', invoicesData?.id],
    () => request.get(`representante/invoices/${invoicesData?.id}/mgm-referrals`, { token }),
    {
      enabled: collapseOpen && !!invoicesData?.id && props.parcelasFilter !== '2',
      staleTime: FIVE_MINUTES,
      cacheTime: FIVE_MINUTES * 2,
    },
  );

  const parcelasResumo = useMemo(() => {
    if (!payments || payments.length === 0) {
      return { primeirasParcelas: 0, segundasParcelas: 0 };
    }

    return payments.reduce(
      (acc, payment) => {
        if (payment.installmentType === 'CONTRACT_SIGNING') {
          acc.primeirasParcelas += 1;
        } else if (payment.installmentType === 'FIRST_BILL_PAID') {
          acc.segundasParcelas += 1;
        }
        return acc;
      },
      { primeirasParcelas: 0, segundasParcelas: 0 },
    );
  }, [payments]);

  const paymentsWithValidation = useMemo(() => {
    if (!payments.length) {
      return [];
    }

    const validationByPaymentId = paymentValidations.reduce((acc, payment) => {
      if (payment?.id && typeof payment?.isValidated === 'boolean') {
        acc[payment.id] = payment.isValidated;
      }

      return acc;
    }, {});

    return payments.map(payment => ({
      ...payment,
      isValidated:
        typeof validationByPaymentId[payment.id] === 'boolean'
          ? validationByPaymentId[payment.id]
          : payment?.isValidated,
    }));
  }, [paymentValidations, payments]);

  const combinedData = [
    ...paymentsWithValidation.map(item => ({ ...item, type: 'payment' })),
    ...campaigns.map(item => ({ ...item, type: 'campaign' })),
    ...bonuses.map(item => ({ ...item, type: 'bonus' })),
    ...referrerData.map(item => ({ ...item, type: 'referrer' })),
    ...mgmIndirectData.map(item => ({ ...item, type: 'mgm_indirect' })),
  ];

  const getCustomerName = useCallback(item => {
    const isMGM = item.type === 'mgm_indirect';
    const isPayment = item.type === 'payment';
    const isReferrer = item.type === 'referrer';
    const isBonus = item.type === 'bonus';

    if (isMGM) {
      return (
        item?.indicatorInfo?.fullName ||
        item?.indicatorInfo?.businessName ||
        item?.indicatorInfo?.customerName ||
        item?.metadata?.customerName ||
        '--'
      );
    }
    if (isPayment || isReferrer) {
      return item?.comissioningLead?.companyName || item?.comissioningLead?.customerName || '--';
    }
    if (isBonus) {
      return item?.bonusName || '--';
    }
    return item?.name || '--';
  }, []);

  const getInstallmentValue = useCallback(item => {
    const isPayment = item.type === 'payment';
    const isBonus = item.type === 'bonus';
    const installmentType = item?.installmentType;
    const installmentPercentage = item?.installmentPercentage;

    if (isBonus) {
      return 1;
    }

    if (!isPayment) {
      return 0;
    }

    if (installmentType === 'CONTRACT_SIGNING' && installmentPercentage === 100) {
      return 1;
    }
    if (installmentType === 'CONTRACT_SIGNING' && installmentPercentage !== 100) {
      return 2;
    }
    if (installmentType === 'FIRST_BILL_PAID') {
      return 3;
    }
    return 0;
  }, []);

  const sortedData = useMemo(() => {
    if (!sortOrder && !sortOrderInstallment) {
      return combinedData;
    }

    const sorted = [...combinedData].sort((a, b) => {
      if (sortOrder) {
        const nameA = getCustomerName(a).toLowerCase();
        const nameB = getCustomerName(b).toLowerCase();

        if (nameA < nameB) {
          return sortOrder === 'ASC' ? -1 : 1;
        }
        if (nameA > nameB) {
          return sortOrder === 'ASC' ? 1 : -1;
        }
        if (nameA === nameB && sortOrderInstallment) {
          const installmentA = getInstallmentValue(a);
          const installmentB = getInstallmentValue(b);
          return sortOrderInstallment === 'ASC' ? installmentA - installmentB : installmentB - installmentA;
        }
        return 0;
      }

      if (sortOrderInstallment) {
        const installmentA = getInstallmentValue(a);
        const installmentB = getInstallmentValue(b);

        if (installmentA !== installmentB) {
          return sortOrderInstallment === 'ASC' ? installmentA - installmentB : installmentB - installmentA;
        }
        const nameA = getCustomerName(a).toLowerCase();
        const nameB = getCustomerName(b).toLowerCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      }

      return 0;
    });

    return sorted;
  }, [combinedData, sortOrder, sortOrderInstallment, getCustomerName, getInstallmentValue]);

  const handleToggleCollapse = () => {
    setCollapseOpen(!collapseOpen);
  };

  async function handleDownload() {
    if (hasFile) {
      try {
        const url = `/representante/documentos/${invoicesData?.partner?.accountPublicId}/download/${invoicesData?.documento?.id}`;
        const file = await request.get(url, {
          responseType: 'blob',
        });
        FileDownload.downloadFile(file, 'nota-fiscal-parceiro');
      } catch (error) {
        setSnackbar({ open: true, message: 'Erro ao obter a nota fiscal' });
      }
    }
  }

  const handleGeneratePO = useCallback(async () => {
    try {
      await request.post(`representante/invoices/${invoicesData?.id}/purchase-order`, {}, { token });
      setSnackbar({
        open: true,
        message: 'PO enviada para processamento! Atualizando dados em 5 segundos...',
      });

      setTimeout(() => {
        refetch();
        if (collapseOpen) {
          refetchPayments();
          refetchCampaigns();
          refetchBonuses();
          refetchMGM();
        }
      }, 5000);
    } catch (error) {
      setSnackbar({ open: true, message: 'Erro ao gerar a PO' });
    }
  }, [invoicesData?.id, token, refetch, collapseOpen, refetchPayments, refetchCampaigns, refetchBonuses, refetchMGM]);

  const handleGetSupplier = useCallback(async () => {
    const cnpj = invoicesData?.cnpj || invoicesData?.partner?.identificationNumber;

    if (!cnpj) {
      setSnackbar({ open: true, message: 'CNPJ não disponível' });
      return;
    }
    setLoadingSupplier(true);
    setSupplierData(null);
    try {
      const data = await request.get(`backstage/comunidade/invoices/supplier/${cnpj}`, { token });
      setSupplierData(data);
      setCurrentModal('supplier');
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || 'Erro ao buscar dados do fornecedor na Oracle',
      });
    } finally {
      setLoadingSupplier(false);
    }
  }, [invoicesData?.cnpj, invoicesData?.partner?.identificationNumber, token]);

  const handleGetStatusHistory = useCallback(async () => {
    if (!invoicesData?.id) {
      setSnackbar({ open: true, message: 'ID da invoice não disponível' });
      return;
    }
    setLoadingStatusHistory(true);
    setStatusHistoryData(null);
    try {
      const data = await request.get(`backstage/comunidade/invoices/${invoicesData.id}/status-history`, {
        token,
        params: { page: 1, limit: 100 },
      });
      setStatusHistoryData(data);
      setCurrentModal('status-history');
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || 'Erro ao buscar histórico de status',
      });
    } finally {
      setLoadingStatusHistory(false);
    }
  }, [invoicesData?.id, token]);

  const handleGetPO = useCallback(async () => {
    if (!invoicesData?.purchaseOrderNumber) {
      setSnackbar({ open: true, message: 'Número da PO não disponível' });
      return;
    }
    setLoadingPO(true);
    setPoData(null);
    try {
      const data = await request.get(
        `backstage/comunidade/invoices/purchase-order/${invoicesData.purchaseOrderNumber}`,
        {
          token,
        },
      );
      setPoData(data);
      setCurrentModal('purchase-order');
    } catch (error) {
      let errorMessage = 'Erro ao buscar dados da PO na Oracle';

      if (error?.message) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
      });
    } finally {
      setLoadingPO(false);
    }
  }, [invoicesData?.purchaseOrderNumber, token]);

  const handleClonePO = useCallback(
    async activityType => {
      const data = await request.post(
        `backstage/comunidade/invoices/${invoicesData?.id}/clone-purchase-order`,
        { activityType },
        { token },
      );
      setSnackbar({
        open: true,
        message: `Nova PO ${data.purchaseOrderNumber} criada com sucesso!`,
      });
      setTimeout(() => refetch(), 2000);
    },
    [invoicesData?.id, token, refetch],
  );

  const uploaderRef = useRef();

  return (
    <>
      <TableRow onClick={handleToggleCollapse} className={classes.tableRowStyle} style={{ cursor: 'pointer' }}>
        <TableCell width="15%">
          <Typography variant="body2">
            {invoicesData?.partner?.businessName || invoicesData?.partner?.tradeName}
          </Typography>
          <Typography>
            <Box display="flex" component="span" className={classes.orgLine}>
              <Box display="flex" alignItems="center" component="span">
                <Typography variant="caption" className={classes.cnpjCaption}>
                  {invoicesData?.cnpj}
                </Typography>
              </Box>
            </Box>
          </Typography>
        </TableCell>
        <TableCell width="15%">
          <Typography className={classes.tableCell} variant="body2">
            {invoicesData?.purchaseOrderNumber ? invoicesData?.purchaseOrderNumber : '--'}
          </Typography>
        </TableCell>

        <TableCell width="15%">
          <Typography className={classes.tableCell} variant="body2">
            {invoicesData?.status}
          </Typography>
        </TableCell>

        <TableCell width="15%">
          <Typography className={classes.tableCell} variant="body2">
            {invoicesData?.oracleInvoiceStatus ?? '--'}
          </Typography>
        </TableCell>

        <TableCell width="15%">
          <Typography className={classes.tableCell} variant="body2">
            {formatAmount(invoicesData?.amount)}
          </Typography>
        </TableCell>

        <TableCell width="15%">
          <Typography className={classes.tableCell} variant="body2">
            {DateTime.fromISO(invoicesData?.referenceDate).toFormat('dd/MM/yyyy')}
          </Typography>
        </TableCell>

        <TableCell width="15%">
          <Typography className={classes.tableCell} variant="body2">
            {invoicesData?.issueDate ? DateTime.fromISO(invoicesData?.issueDate).toFormat('dd/MM/yyyy') : '--'}
          </Typography>
        </TableCell>

        <TableCell width="15%">
          <Typography className={classes.tableCell} variant="body2">
            {invoicesData?.paymentDate ? DateTime.fromISO(invoicesData?.paymentDate).toFormat('dd/MM/yyyy') : '--'}
          </Typography>
        </TableCell>

        <TableCell width="15%">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: hasFile ? 'pointer' : 'default',
            }}
            onClick={handleDownload}
          >
            <DownloadIcon color={hasFile ? '' : '#00000061'} />
            <Typography className={classes.tableCell} variant="body2">
              {hasFile ? `1` : `0`} arquivo
            </Typography>
          </Box>
        </TableCell>

        <TableCell className={classes.tableCell} onClick={event => event.stopPropagation()}>
          <MenuButton
            menuOptions={[
              {
                title: 'Adicionar valor à comissão',
                action: () => setCurrentModal('adicionar'),
                disabled: ![
                  PartnerInvoiceStatus.AGUARDANDO_VALIDACAO_INTERNA,
                  PartnerInvoiceStatus.AGUARDANDO_VALIDACAO_PARCEIRO,
                ].includes(invoicesData.status),
              },
              {
                title: 'Adicionar campanha à comissão',
                action: () => setCurrentModal('campanha'),
                disabled: ![
                  PartnerInvoiceStatus.AGUARDANDO_VALIDACAO_INTERNA,
                  PartnerInvoiceStatus.AGUARDANDO_VALIDACAO_PARCEIRO,
                ].includes(invoicesData.status),
              },
              {
                title: 'Editar pagamento',
                action: () => setCurrentModal('editar'),
              },
              {
                title: 'Enviar nota fiscal',
                action: () => uploaderRef.current?.openFileDialog(),
              },
              {
                title: 'Gerar PO',
                action: () => handleGeneratePO(),
                disabled: invoicesData.status !== PartnerInvoiceStatus.PENDENTE_GERACAO_OC,
              },
              {
                title: 'Consultar Fornecedor na Oracle',
                action: () => handleGetSupplier(),
                disabled: loadingSupplier,
              },
              {
                title: 'Consultar PO na Oracle',
                action: () => handleGetPO(),
                disabled: !invoicesData?.purchaseOrderNumber || loadingPO,
              },
              {
                title: 'Histórico de Status',
                action: () => handleGetStatusHistory(),
                disabled: loadingStatusHistory,
              },
              {
                title: 'Recriar PO com novo CNAE',
                action: () => setCurrentModal('clone-po'),
                disabled: !invoicesData?.purchaseOrderNumber,
              },
            ]}
          />
        </TableCell>
      </TableRow>

      <TableRow style={{ display: collapseOpen ? 'contents' : 'none' }}>
        <TableCell colSpan={10} style={{ padding: '0px' }}>
          <Collapse in={collapseOpen} timeout="auto" unmountOnExit>
            <Box
              display="flex"
              width="100%"
              className={classes.tableEmpresas}
              sx={{ gap: '14px', padding: '16px', paddingLeft: '20px', paddingRight: '20px' }}
            >
              <Typography
                align="left"
                variant="subtitle2"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                CNAE:
                <Typography component="span" variant="body2">
                  {invoicesData?.partner?.activityType || 'Não cadastrado'}
                </Typography>
              </Typography>

              <Typography
                align="left"
                variant="subtitle2"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '6px',
                  flex: 1,
                }}
              >
                Observações:
                <Typography component="span" variant="body2">
                  {invoicesData?.comment || '-'}
                </Typography>
              </Typography>

              <Typography
                variant="subtitle2"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '6px',
                }}
              >
                Qt. 1ª Parcela:
                <Typography component="span" variant="body2" color="primary" style={{ fontWeight: '500' }}>
                  {parcelasResumo.primeirasParcelas}
                </Typography>
              </Typography>

              <Typography
                variant="subtitle2"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '6px',
                }}
              >
                Qt. 2ª Parcela:
                <Typography component="span" variant="body2" color="primary" style={{ fontWeight: '500' }}>
                  {parcelasResumo.segundasParcelas}
                </Typography>
              </Typography>

              <Typography
                align="left"
                variant="subtitle2"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '6px',
                }}
              >
                Número da Nota:
                <Typography component="span" variant="body2" color="primary" style={{ fontWeight: '500' }}>
                  {invoicesData.invoiceNumber}
                </Typography>
              </Typography>
              <Typography
                align="left"
                variant="subtitle2"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '6px',
                }}
              >
                Entrada no sistema:
                <Typography component="span" variant="body2" color="primary" style={{ fontWeight: '500' }}>
                  {invoicesData?.uploadDate ? DateTime.fromISO(invoicesData?.uploadDate).toFormat('dd/MM/yyyy') : '--'}
                </Typography>
              </Typography>
            </Box>
            <Divider />

            <Table>
              <TableHead className={classes.tableHead}>
                <TableRow className={classes.rowShadow}>
                  <TableCell className={classes.tableCell}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle2">Cliente</Typography>
                      <TableColumnSortButton value={sortOrder || ''} onChange={setSortOrder} />
                    </Box>
                  </TableCell>

                  <TableCell className={classes.tableCell}>
                    <Typography variant="subtitle2">Tipo de Ganho</Typography>
                  </TableCell>

                  <TableCell className={classes.tableCell}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle2">Parcela</Typography>
                      <TableColumnSortButton value={sortOrderInstallment || ''} onChange={setSortOrderInstallment} />
                    </Box>
                  </TableCell>

                  <TableCell className={classes.tableCell}>
                    <Typography variant="subtitle2">Valor da Parcela</Typography>
                  </TableCell>

                  <TableCell className={classes.tableCell}>
                    <Typography variant="subtitle2">% da Comissão</Typography>
                  </TableCell>

                  <TableCell className={classes.tableCell}>
                    <Typography variant="subtitle2">Data referência</Typography>
                  </TableCell>

                  <TableCell colSpan={2} className={classes.tableCell}>
                    <Typography variant="subtitle2">Distribuidora</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoading ? (
                  <TableRow width="100%" className={classes.tr}>
                    <TableCell className={classes.tableCell} colSpan={9} align="center">
                      <Typography>Carregando pagamentos...</Typography>
                    </TableCell>
                  </TableRow>
                ) : sortedData && sortedData.length > 0 ? (
                  sortedData.map((row, index) => (
                    <ParceirosPagamentosTableRow
                      key={row.id}
                      paymentsData={row}
                      invoiceData={invoicesData}
                      campaigns={campaigns}
                      isFirstRow={index === 0}
                      isLastRow={index === sortedData.length - 1}
                      refetchInvoices={refetch}
                    />
                  ))
                ) : (
                  <TableRow width="100%" className={classes.tr}>
                    <TableCell className={classes.tableCell} colSpan={9} align="center">
                      Ops. Não encontramos nada.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
      <Portal>
        <FileUploader
          ref={uploaderRef}
          partnerPublicId={invoicesData?.partner?.accountPublicId}
          invoiceId={invoicesData?.id}
          token={token}
          documentoRepresentanteId={invoicesData?.documentoRepresentanteId}
          refetch={refetch}
        />

        <ModalEditarDados
          isOpen={currentModal === 'editar'}
          onClose={handleCloseModal}
          id={invoicesData?.id}
          invoiceData={invoicesData}
          refetch={refetch}
        />
        <ModalAdicionarComissao
          isOpen={currentModal === 'adicionar'}
          onClose={handleCloseModal}
          id={invoicesData?.id}
          refetch={refetchInvoiceAndPayments}
        />
        <ModalAdicionarCampanha
          isOpen={currentModal === 'campanha'}
          onClose={handleCloseModal}
          id={invoicesData?.id}
          refetch={refetch}
          refetchCampaigns={refetchCampaigns}
        />
        <ModalSupplierData
          isOpen={currentModal === 'supplier'}
          onClose={handleCloseModal}
          supplierData={supplierData}
          isLoading={loadingSupplier}
        />
        <ModalPurchaseOrderData
          isOpen={currentModal === 'purchase-order'}
          onClose={handleCloseModal}
          poData={poData}
          isLoading={loadingPO}
        />
        <ModalStatusHistory
          isOpen={currentModal === 'status-history'}
          onClose={handleCloseModal}
          statusHistoryData={statusHistoryData}
          isLoading={loadingStatusHistory}
          invoiceCreatedAt={invoicesData?.createdAt}
          invoiceInitialStatus={invoicesData?.status}
        />
        <ModalClonePurchaseOrder
          isOpen={currentModal === 'clone-po'}
          onClose={handleCloseModal}
          invoiceData={invoicesData}
          onSuccess={handleClonePO}
        />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={2000}
          message={snackbar.message}
          onClose={() => setSnackbar({ open: false, message: '' })}
        />
      </Portal>
    </>
  );
}

ParceirosTableRow.propTypes = {
  invoicesData: PropTypes.shape({
    partner: PropTypes.object,
    purchaseOrderNumber: PropTypes.string,
    status: PropTypes.string,
    amount: PropTypes.number,
    referenceDate: PropTypes.string,
    issueDate: PropTypes.string,
    paymentDate: PropTypes.string,
    refetch: PropTypes.func,
  }).isRequired,
};
